/**
 * Trunnion AI - Error Fallback Component
 * Styled error display shown when the ErrorBoundary catches an error.
 * Provides retry and navigate-to-dashboard actions.
 *
 * Note: This component uses window.location for the "Go to Dashboard" action
 * instead of react-router's useNavigate, because ErrorFallback may be rendered
 * outside of a Router context (e.g., at the app root). Using a full page
 * navigation also ensures a clean state reset after a critical error.
 */

import { useState } from 'react';
import { AlertTriangle, RotateCcw, Home, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { ROUTES } from '@/config/routes';

interface ErrorFallbackProps {
  /** The error that was caught. */
  error: Error | null;
  /** Optional callback to retry rendering. Falls back to full page reload. */
  onRetry?: () => void;
}

export function ErrorFallback({ error, onRetry }: ErrorFallbackProps) {
  const [stackOpen, setStackOpen] = useState(false);

  const handleRetry = () => {
    if (onRetry) {
      onRetry();
    } else {
      window.location.reload();
    }
  };

  const handleGoHome = () => {
    // Use full navigation to ensure a clean state after a critical error
    window.location.href = ROUTES.DASHBOARD;
  };

  return (
    <div className="flex min-h-100 items-center justify-center p-6">
      <div className="w-full max-w-lg rounded-xl border border-trunnion-slate-700 bg-trunnion-slate-800 p-8">
        {/* Icon */}
        <div className="mb-5 flex justify-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-red-500/10">
            <AlertTriangle className="h-7 w-7 text-red-400" />
          </div>
        </div>

        {/* Title */}
        <h2 className="mb-2 text-center text-xl font-semibold text-white">
          Something went wrong
        </h2>

        {/* Error message */}
        <p className="mb-6 text-center text-sm text-trunnion-slate-300">
          {error?.message || 'An unexpected error occurred. Please try again or return to the dashboard.'}
        </p>

        {/* Stack trace (collapsible) */}
        {error?.stack && (
          <div className="mb-6">
            <button
              onClick={() => setStackOpen(!stackOpen)}
              className="flex w-full items-center justify-between rounded-lg px-3 py-2 text-xs font-medium text-trunnion-slate-400 hover:bg-trunnion-slate-700 transition-colors"
            >
              <span>Error Details</span>
              {stackOpen ? (
                <ChevronUp className="h-3.5 w-3.5" />
              ) : (
                <ChevronDown className="h-3.5 w-3.5" />
              )}
            </button>
            {stackOpen && (
              <pre className="mt-2 max-h-48 overflow-auto rounded-lg border border-trunnion-slate-700 bg-trunnion-slate-900 p-3 font-mono text-xs leading-relaxed text-trunnion-slate-300">
                {error.stack}
              </pre>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-col gap-2 sm:flex-row sm:justify-center">
          <Button
            variant="primary"
            icon={<RotateCcw className="h-4 w-4" />}
            onClick={handleRetry}
          >
            Try Again
          </Button>
          <Button
            variant="ghost"
            icon={<Home className="h-4 w-4" />}
            onClick={handleGoHome}
          >
            Go to Dashboard
          </Button>
        </div>
      </div>
    </div>
  );
}
